import React, { useCallback, useEffect, useLayoutEffect } from "react";
import {
  Button,
  Container,
  Icon,
  Layout,
  MailtoLink,
  Row,
} from "@openedx/paragon";
import { Add as AddIcon, Error } from "@openedx/paragon/icons";
import { useIntl } from "@edx/frontend-platform/i18n";
import { StudioFooter } from "@edx/frontend-component-footer";
import { getConfig } from "@edx/frontend-platform";
import { useLocation, useNavigate } from "react-router-dom";

import Loading from "../generic/Loading";
import InternetConnectionAlert from "../generic/internet-connection-alert";
import Header from "../header";
import SubHeader from "../generic/sub-header/SubHeader";
import HomeSidebar from "./home-sidebar";
import TabsSection from "./tabs-section";
import OrganizationSection from "./organization-section";
import VerifyEmailLayout from "./verify-email-layout";
import CreateNewCourseForm from "./create-new-course-form";
import messages from "./messages";
import { useStudioHome } from "./hooks";
import AlertMessage from "../generic/alert-message";
import CustomFooter from "../custom-footer/custom-footer";
import ChatBox from '../layout/chatbot/Chatbot';

const AiStudioIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.0421 10.0003C12.2142 11.2648 11.1891 12.2898 9.92461 18.1178C8.66013 12.2898 7.63507 11.2648 1.80713 10.0003C7.63507 8.73581 8.66013 7.71075 9.92461 1.88281C11.1891 7.71075 12.2142 8.73581 18.0421 10.0003Z"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.7776 2.44141V6.15367"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.9215 4.29785H17.6338"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.59495 15.3418C4.10111 15.3418 4.51143 14.9314 4.51143 14.4253C4.51143 13.9191 4.10111 13.5088 3.59495 13.5088C3.08879 13.5088 2.67847 13.9191 2.67847 14.4253C2.67847 14.9314 3.08879 15.3418 3.59495 15.3418Z"
      stroke="white"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StudioHome = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  const isPaginationCoursesEnabled = getConfig().ENABLE_HOME_PAGE_COURSE_API_V2;
  const {
    isLoadingPage,
    isFailedLoadingPage,
    studioHomeData,
    isShowProcessing,
    anyQueryIsFailed,
    isShowEmailStaff,
    anyQueryIsPending,
    showNewCourseContainer,
    isShowOrganizationDropdown,
    hasAbilityToCreateNewCourse,
    isFiltered,
    setShowNewCourseContainer,
    librariesV1Enabled,
    librariesV2Enabled,
  } = useStudioHome();

  const v1LibraryTab =
    librariesV1Enabled &&
    location?.pathname.split("/").pop() === "libraries-v1";
  const showV2LibraryURL = librariesV2Enabled && !v1LibraryTab;

  const {
    userIsActive,
    studioShortName,
    studioRequestEmail,
    showNewLibraryButton,
    showNewLibraryV2Button,
  } = studioHomeData;

  const getHeaderButtons = useCallback(() => {
    const headerButtons: JSX.Element[] = [];

    if (isFailedLoadingPage || !userIsActive) {
      return headerButtons;
    }

    if (isShowEmailStaff) {
      headerButtons.push(
        <MailtoLink to={studioRequestEmail}>
          {intl.formatMessage(messages.emailStaffBtnText)}
        </MailtoLink>
      );
    }

    if ((hasAbilityToCreateNewCourse && getConfig().AISTUDIO_ENABLE) || true) {
      headerButtons.push(
        <button
          disabled={false}
          className="btn"
          onClick={() => {
            window.location.href = `${getConfig().MFE_AISTUDIO_HOME_PAGE_URL}`;
          }}
        >
          {AiStudioIcon}
          Ai Studio
        </button>
      );
    }

    if (hasAbilityToCreateNewCourse) {
      headerButtons.push(
        <Button
          variant="outline-primary"
          iconBefore={AddIcon}
          size="sm"
          disabled={showNewCourseContainer}
          onClick={() => setShowNewCourseContainer(true)}
        >
          {intl.formatMessage(messages.addNewCourseBtnText)}
        </Button>
      );
    }

    if (
      (showNewLibraryButton && !showV2LibraryURL) ||
      (showV2LibraryURL && showNewLibraryV2Button)
    ) {
      const newLibraryClick = () => {
        if (showV2LibraryURL) {
          navigate("/library/create");
        } else {
          // Studio home library for legacy libraries
          window.open(`${getConfig().STUDIO_BASE_URL}/home_library`);
        }
      };

      headerButtons.push(
        <Button
          variant="outline-primary"
          iconBefore={AddIcon}
          size="sm"
          onClick={newLibraryClick}
          data-testid="new-library-button"
        >
          {intl.formatMessage(messages.addNewLibraryBtnText)}
        </Button>
      );
    }

    return headerButtons;
  }, [location, userIsActive, isFailedLoadingPage]);

  useLayoutEffect(() => {
    const updateMinHeight = () => {
      const headerHeight = (document.querySelector(".studio-header") as HTMLElement)?.offsetHeight || 0;
      const footerHeight = (document.querySelector("footer") as HTMLElement)?.offsetHeight || 0;


      const mainContent = document.querySelector(".main-content");
      if (mainContent instanceof HTMLElement) {
        mainContent.style.minHeight = `calc(100vh - ${headerHeight + footerHeight}px)`;
      }
    };

    updateMinHeight();
    window.addEventListener("resize", updateMinHeight);

    return () => window.removeEventListener("resize", updateMinHeight);
  }, [isLoadingPage, isFiltered]);

  const headerButtons = userIsActive ? getHeaderButtons() : [];
  if (isLoadingPage && !isFiltered) {
    return <Loading />;
  }

  const getMainBody = () => {
    if (isFailedLoadingPage) {
      return (
        <AlertMessage
          variant="danger"
          description={
            <Row className="m-0 align-items-center">
              <Icon src={Error} className="text-danger-500 mr-1" />
              <span>
                {intl.formatMessage(messages.homePageLoadFailedMessage)}
              </span>
            </Row>
          }
        />
      );
    }
    if (!userIsActive) {
      return <VerifyEmailLayout />;
    }
    return (
      <Layout
        lg={[{ span: 9 }, { span: 3 }]}
        md={[{ span: 9 }, { span: 3 }]}
        sm={[{ span: 9 }, { span: 3 }]}
        xs={[{ span: 9 }, { span: 3 }]}
        xl={[{ span: 9 }, { span: 3 }]}
      >
        <Layout.Element>
          <section>
            {showNewCourseContainer && (
              <CreateNewCourseForm
                handleOnClickCancel={() => setShowNewCourseContainer(false)}
              />
            )}
            {isShowOrganizationDropdown && <OrganizationSection />}
            <TabsSection
              showNewCourseContainer={showNewCourseContainer}
              onClickNewCourse={() => setShowNewCourseContainer(true)}
              isShowProcessing={isShowProcessing && !isFiltered}
              isPaginationCoursesEnabled={isPaginationCoursesEnabled}
              librariesV1Enabled={librariesV1Enabled}
              librariesV2Enabled={librariesV2Enabled}
            />
          </section>
        </Layout.Element>
        <Layout.Element>
          <HomeSidebar />
        </Layout.Element>
      </Layout>
    );
  };

  return (
    <>
      <Header isHiddenMainMenu />
      <div className="main-content">
        <Container size="xl" className="p-4 mt-3">
          <section className="mb-4">
            <article className="studio-home-sub-header">
              <section>
                <SubHeader
                  title={intl.formatMessage(messages.headingTitle, {
                    studioShortName: studioShortName || "Studio",
                  })}
                  headerActions={headerButtons}
                />
              </section>
            </article>
            {getMainBody()}
          </section>
        </Container>
        <div className="alert-toast">
          <InternetConnectionAlert
            isFailed={anyQueryIsFailed}
            isQueryPending={anyQueryIsPending}
          />
        </div>
      </div>
      {getConfig().AISTUDIO_ENABLE && <ChatBox />}
      
      {/* <StudioFooter /> */}
      <CustomFooter />
    </>
  );
};

export default StudioHome;
